/**
 * Changer le rôle ou le statut d'un membre — chunk 9.AE.1.
 *
 * Le serveur refuse l'escalade et le changement de son propre rôle. Ce dialogue
 * n'est jamais ouvert sur soi-même (le bouton est grisé), et ne propose que les
 * rôles que l'acteur peut réellement assigner.
 *
 * Réactiver quelqu'un passe par le même endpoint : `status` et `role` voyagent
 * ensemble dans `PATCH /:id/role`. C'est ce qui rend la réactivation possible sans
 * qu'une route de plus soit nécessaire.
 */
import { useId, useState } from "react";
import { useTranslation } from "react-i18next";

import { useAuth } from "../../../contexts/AuthContext";
import { getApiErrorMessage, normalizeLang } from "../../../lib/apiErrorMessage";
import {
  useUpdateMemberRole,
  type TeamMember,
  type TeamRole,
  type TeamStatus,
} from "../../../lib/queries/team";

export function MemberRoleDialog({
  membre,
  onClose,
}: {
  readonly membre: TeamMember;
  readonly onClose: () => void;
}) {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const idTitre = useId();
  const idRole = useId();
  const idStatut = useId();

  const [role, setRole] = useState<TeamRole>(membre.role);
  const [statut, setStatut] = useState<TeamStatus>(membre.status);
  const [erreur, setErreur] = useState<string | null>(null);
  const majRole = useUpdateMemberRole();

  const rolesPossibles: TeamRole[] =
    user?.role === "super_admin"
      ? membre.role === "super_admin"
        ? ["super_admin"]
        : ["tenant_admin", "broker", "client_contact"]
      : ["broker", "client_contact"];

  // Un `tenant_admin` ne peut pas rétrograder un autre administrateur : le serveur
  // refuserait l'assignation d'un rôle qu'il ne peut pas donner, et le rôle actuel
  // n'est pas dans sa liste. On le dit plutôt que d'afficher un menu inopérant.
  const horsPortee = !rolesPossibles.includes(membre.role);

  async function soumettre(e: React.FormEvent) {
    e.preventDefault();
    setErreur(null);
    try {
      await majRole.mutateAsync({
        id: membre.id,
        ...(role !== membre.role ? { role } : {}),
        ...(statut !== membre.status ? { status: statut } : {}),
      });
      onClose();
    } catch (err) {
      setErreur(
        getApiErrorMessage(err, normalizeLang(i18n.resolvedLanguage)) ||
          t("team.role_dialog.error"),
      );
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby={idTitre}
    >
      <form
        onSubmit={soumettre}
        className="w-full max-w-md rounded-xl border border-slate-200 bg-white p-5 shadow-lg"
      >
        <h2 id={idTitre} className="text-base font-semibold text-slate-900">
          {t("team.role_dialog.title")}
        </h2>
        <p className="mt-1 text-sm text-slate-600">{membre.full_name ?? membre.email}</p>

        {horsPortee ? (
          <p className="mt-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
            {t("team.role_dialog.out_of_reach")}
          </p>
        ) : (
          <>
            <label htmlFor={idRole} className="mt-4 block text-sm font-medium text-slate-700">
              {t("team.role_dialog.role")}
            </label>
            <select
              id={idRole}
              value={role}
              onChange={(e) => setRole(e.target.value as TeamRole)}
              className="focus-visible:border-brand-500 focus-visible:ring-brand-500 mt-1 block w-full rounded-lg border border-slate-300 px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2"
            >
              {rolesPossibles.map((r) => (
                <option key={r} value={r}>
                  {t(`team.role.${r}`)}
                </option>
              ))}
            </select>
          </>
        )}

        <label htmlFor={idStatut} className="mt-4 block text-sm font-medium text-slate-700">
          {t("team.role_dialog.status")}
        </label>
        <select
          id={idStatut}
          value={statut}
          onChange={(e) => setStatut(e.target.value as TeamStatus)}
          className="focus-visible:border-brand-500 focus-visible:ring-brand-500 mt-1 block w-full rounded-lg border border-slate-300 px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2"
        >
          {(["active", "pending", "disabled"] as const).map((s) => (
            <option
              key={s}
              value={s}
              disabled={
                membre.role !== "super_admin" &&
                ((s === "active" && membre.status !== "active" && !membre.accepted_at) ||
                  (s === "pending" && membre.status === "active"))
              }
            >
              {t(`team.status.${s}`)}
            </option>
          ))}
        </select>

        {membre.role !== "super_admin" && membre.status !== "active" && !membre.accepted_at && (
          <p className="mt-2 text-sm text-amber-900">{t("membership.admin_acceptance_required")}</p>
        )}

        {erreur !== null && (
          <p className="mt-3 text-sm text-red-700" role="alert">
            {erreur}
          </p>
        )}

        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="focus-visible:ring-brand-500 rounded-lg border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2"
          >
            {t("common.cancel")}
          </button>
          <button
            type="submit"
            disabled={
              majRole.isPending || horsPortee || (role === membre.role && statut === membre.status)
            }
            className="bg-brand-600 hover:bg-brand-700 focus-visible:ring-brand-500 rounded-lg px-3 py-1.5 text-sm font-medium text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:opacity-50"
          >
            {majRole.isPending ? t("team.role_dialog.saving") : t("team.role_dialog.submit")}
          </button>
        </div>
      </form>
    </div>
  );
}
